import React, { useState, useEffect } from 'react';
import {
    Button, Card, Grid, makeStyles, TextField,
    Typography, List, ListItem, ListItemText, IconButton, Divider, Tooltip
} from '@material-ui/core';
import { Delete, Refresh, PlayArrow, PersonAdd, LockOpen } from '@material-ui/icons';
import ProjetosHeader from '../components/projetoComponets/ProjetosHeader';
import { EnumColors } from '../enum/EnumColors';
import { db } from '../firebaseConfig';
import { ocultar } from '../Utils';

const useStyles = makeStyles((theme) => ({
    cardPadding: { padding: '30px', marginTop: '20px', borderRadius: '15px' },
    inputMargin: { marginRight: '10px' },
    actions: { marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' },
    btnSorteio: {
        backgroundColor: EnumColors.PURPLE_MIDLE,
        color: EnumColors.WHITE,
        '&:hover': { backgroundColor: '#4b0082' }
    },
    btnReset: { color: theme.palette.error.main }
}));

const AmigoSecretoAdmin: React.FC = () => {
    const classes = useStyles();
    const [novoNome, setNovoNome] = useState('');
    const [participantes, setParticipantes] = useState<any[]>([]);

    useEffect(() => {
        const unsub = db.collection('participantes').onSnapshot(snapshot => {
            const lista = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setParticipantes(lista);
        });
        return () => unsub();
    }, []);

    const adicionarParticipante = async () => {
        if (!novoNome.trim()) return;
        try {
            await db.collection('participantes').add({
                nome: novoNome.trim(),
                senha: '',
                sorteou: null,
                foiSorteado: false,
                visualizou: false
            });
            setNovoNome('');
        } catch (err) {
            console.error("Erro ao adicionar:", err);
        }
    };

    const removerParticipante = async (id: string) => {
        if (window.confirm("Deseja realmente remover este participante?")) {
            await db.collection('participantes').doc(id).delete();
        }
    };

    // NOVA FUNÇÃO: Reseta apenas o PIN de um usuário específico
    const resetarSenhaUnica = async (id: string, nome: string) => {
        if (window.confirm(`Deseja apagar o PIN de ${nome}? Ele poderá cadastrar um novo no próximo acesso.`)) {
            await db.collection('participantes').doc(id).update({
                senha: ''
            });
        }
    };

    // FUNÇÃO ATUALIZADA: Reinicia o sorteio mas MANTÉM as senhas
    const reiniciarSorteio = async () => {
        if (!window.confirm("ATENÇÃO: Isso apagará os resultados do sorteio, mas manterá os nomes e PINs cadastrados. Continuar?")) return;

        const batch = db.batch();
        participantes.forEach(p => {
            batch.update(db.collection('participantes').doc(p.id), {
                sorteou: null,
                foiSorteado: false,
                visualizou: false
                // A linha de senha foi removida para manter o PIN existente
            });
        });
        await batch.commit();
        alert("Sorteio reiniciado! Os PINs foram preservados.");
    };
    const gerarSorteioCompleto = async () => {
        if (participantes.length < 3) return alert("Mínimo de 3 pessoas para sortear.");

        // Função interna para embaralhamento robusto (Fisher-Yates)
        const shuffle = (array: any[]) => {
            const novoArray = [...array];
            for (let i = novoArray.length - 1; i > 0; i--) {
                // Gera um número aleatório muito mais preciso
                const randomBuffer = new Uint32Array(1);
                window.crypto.getRandomValues(randomBuffer);
                const j = randomBuffer[0] % (i + 1);

                [novoArray[i], novoArray[j]] = [novoArray[j], novoArray[i]];
            }
            return novoArray;
        };

        let sorteioValido = false;
        let resultado: any[] = [];
        let tentativas = 0;

        // Aumentamos o limite de tentativas para garantir sucesso em grupos grandes
        while (!sorteioValido && tentativas < 1000) {
            tentativas++;

            // Embaralhamos a lista de IDs
            const candidatos = participantes.map(p => p.id);
            const embaralhado = shuffle(candidatos);

            // Verifica a regra: ninguém pode tirar a si mesmo
            // p.id é o doador, embaralhado[index] é o amigo secreto
            if (participantes.every((p, index) => p.id !== embaralhado[index])) {
                resultado = participantes.map((p, index) => ({
                    idDono: p.id,
                    idAmigo: embaralhado[index]
                }));
                sorteioValido = true;
            }
        }

        if (!sorteioValido) {
            return alert("Erro matemático ao gerar combinações sem repetição. Tente novamente.");
        }

        try {
            const batch = db.batch();
            resultado.forEach(par => {
                batch.update(db.collection('participantes').doc(par.idDono), {
                    sorteou: ocultar(par.idAmigo),
                    visualizou: false
                });
                batch.update(db.collection('participantes').doc(par.idAmigo), {
                    foiSorteado: true
                });
            });

            await batch.commit();
            alert("Sorteio ultra-aleatório realizado com sucesso! ✨");
        } catch (error) {
            console.error("Erro ao salvar sorteio:", error);
            alert("Erro ao conectar com o banco de dados.");
        }
    };

    return (
        <>
            <ProjetosHeader />
            <Grid container justifyContent="center" style={{ padding: '20px' }}>
                <Grid item xs={12} md={8}>
                    <Card className={classes.cardPadding} variant="outlined">
                        <Typography variant="h4" gutterBottom>Painel Administrativo 🛠️</Typography>

                        <div style={{ display: 'flex', marginTop: '20px', marginBottom: '30px' }}>
                            <TextField
                                label="Nome do Amigo"
                                variant="outlined"
                                fullWidth
                                value={novoNome}
                                onChange={(e) => setNovoNome(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && adicionarParticipante()}
                                className={classes.inputMargin}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={adicionarParticipante}
                                startIcon={<PersonAdd />}
                            >
                                Adicionar
                            </Button>
                        </div>

                        <Divider />

                        <List>
                            {participantes.length === 0 && <Typography style={{ padding: 20 }}>Nenhum participante cadastrado.</Typography>}
                            {participantes.map((p) => (
                                <ListItem key={p.id} divider>
                                    <ListItemText
                                        primary={<strong>{p.nome}</strong>}
                                        secondary={
                                            <>
                                                {p.senha ? "✅ PIN Definido" : "❌ Sem PIN"}
                                                {" | "}
                                                {p.visualizou ? "🎁 Já revelou o amigo" : "⏳ Não revelou ainda"}
                                            </>
                                        }
                                    />

                                    {/* Botão para Resetar apenas a senha deste usuário */}
                                    <Tooltip title="Resetar PIN (Senha)">
                                        <IconButton onClick={() => resetarSenhaUnica(p.id, p.nome)}>
                                            <LockOpen style={{ color: EnumColors.YELLOW }} />
                                        </IconButton>
                                    </Tooltip>

                                    <IconButton color="secondary" onClick={() => removerParticipante(p.id)}>
                                        <Delete />
                                    </IconButton>
                                </ListItem>
                            ))}
                        </List>

                        <div className={classes.actions}>
                            <Button
                                variant="contained"
                                className={classes.btnSorteio}
                                startIcon={<PlayArrow />}
                                onClick={gerarSorteioCompleto}
                                disabled={participantes.length < 3}
                            >
                                Gerar/Refazer Sorteio
                            </Button>

                            <Button
                                variant="outlined"
                                className={classes.btnReset}
                                startIcon={<Refresh />}
                                onClick={reiniciarSorteio}
                            >
                                Resetar Resultados
                            </Button>
                        </div>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default AmigoSecretoAdmin;